import { useEffect, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import { CircularProgress, Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PhoneNumberInput } from '@components/PhoneNumberInput';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { MfaStatus } from '@shared/constants/mfa';
import { isValidE164PhoneNumber } from '@utils/phoneNumberUtils';
import { PhoneEditor, SavePhoneButton } from './styles';

interface PhoneNumberEditorProps {
  userId?: string;
  phone?: string | null;
  mfaStatus?: MfaStatus | null;
  saving?: boolean;
  saveRevision?: number;
  onSave: (userId: string, phoneNumber: string) => void;
}

export const PhoneNumberEditor = ({
  userId,
  phone,
  mfaStatus,
  saving = false,
  saveRevision = 0,
  onSave,
}: PhoneNumberEditorProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [phoneDraft, setPhoneDraft] = useState(() => String(phone || ''));
  const normalizedValue = phoneDraft.trim();
  const savedPhone = String(phone || '').trim();
  const isValid = !normalizedValue || isValidE164PhoneNumber(normalizedValue);
  const hasChanged = normalizedValue !== savedPhone;

  useEffect(() => {
    setPhoneDraft(String(phone || ''));
  }, [phone, saveRevision]);

  const savePhone = () => {
    if (!userId || !isValid || !hasChanged || saving) return;

    const dispatchPhoneUpdate = () => onSave(userId, normalizedValue);
    const isRemovingPhone = Boolean(savedPhone) && !normalizedValue;
    if (isRemovingPhone && mfaStatus === MfaStatus.SMS) {
      setPhoneDraft(savedPhone);
      dispatch(openPopUp(POPUP_TYPES.ALERT, {
        titleText: t('Authentication.MFA.removePhoneBlockedTitle'),
        bodyText: t('Authentication.MFA.removePhoneSmsBlocked'),
        buttonText: t('Actions.Instance.close'),
      }));
      return;
    }

    if (isRemovingPhone && mfaStatus === MfaStatus.EMAIL) {
      dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
        titleText: t('Authentication.MFA.removePhoneConfirmTitle'),
        bodyText: t('Authentication.MFA.emailWithoutPhoneWarning'),
        confirmText: t('Authentication.MFA.continueWithoutPhone'),
        cancelText: t('Actions.Instance.cancel'),
        disableBackdrop: true,
        confirmOnEnter: true,
        onConfirm: (confirm: boolean) => {
          if (confirm) {
            dispatchPhoneUpdate();
          } else {
            setPhoneDraft(savedPhone);
          }
        },
      }));
      return;
    }

    dispatchPhoneUpdate();
  };

  return (
    <PhoneEditor>
      <PhoneNumberInput
        value={phoneDraft}
        onChange={setPhoneDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') savePhone();
        }}
        placeholder={t('Authentication.MFA.phonePlaceholder')}
        ariaLabel={t('Authentication.MFA.phoneLabel')}
        error={!isValid}
        helperText={!isValid ? t('Authentication.MFA.phoneInvalid') : undefined}
        disabled={saving}
      />
      <Tooltip title={t('Actions.Instance.save')} arrow>
        <span>
          <SavePhoneButton
            type="button"
            aria-label={t('Actions.Instance.save')}
            onClick={savePhone}
            disabled={!isValid || !hasChanged || saving}
          >
            {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
          </SavePhoneButton>
        </span>
      </Tooltip>
    </PhoneEditor>
  );
};